import React, { memo, useState, useEffect, useCallback } from 'react';
import { Zap, Pin, PinOff, Clock } from 'lucide-react';
import {
    IndexSymbol,
    SUPPORTED_SYMBOLS,
    STORAGE_KEYS
} from '../config/constants';
import { useSettingsStore } from '../stores/settingsStore';
import { useIndexConstituentsLive } from '../hooks/useIndexConstituentsLive';
import { GeometricAccordion } from './ui/GeometricAccordion';
import { IndexPlCard } from './constituents/IndexPlCard';
import { IndexTopMoversSection } from './constituents/IndexTopMoversSection';

interface IndexConstituentsPlSectionProps {
    className?: string;
    isPinned?: boolean;
    onTogglePin?: () => void;
}

export const IndexConstituentsPlSection: React.FC<IndexConstituentsPlSectionProps> = memo(({
    className = '',
    isPinned: controlledPinned,
    onTogglePin: controlledTogglePin,
}) => {
    const selectedSymbol = useSettingsStore((s) => s.symbol) as IndexSymbol;
    const setSymbol = useSettingsStore((s) => s.setSymbol);

    const { summaries, lastUpdated, isLoading, isLive } = useIndexConstituentsLive(2000);

    const [internalPinned, setInternalPinned] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(STORAGE_KEYS.INDEX_CONSTITUENTS_PL_PINNED) === 'true';
        }
        return false;
    });

    const isActuallyPinned = controlledPinned !== undefined ? controlledPinned : internalPinned;

    const handleTogglePin = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (controlledTogglePin) {
            controlledTogglePin();
        } else {
            setInternalPinned((prev) => {
                const next = !prev;
                if (typeof window !== 'undefined') {
                    localStorage.setItem(STORAGE_KEYS.INDEX_CONSTITUENTS_PL_PINNED, String(next));
                    window.dispatchEvent(new Event('oracle_pinned_changed'));
                }
                return next;
            });
        }
    }, [controlledTogglePin]);

    const handleSelectIndex = useCallback((sym: IndexSymbol) => {
        setSymbol(sym);
    }, [setSymbol]);

    // Format IST Time in HH:MM:SS format
    const istTimeStr = lastUpdated
        ? new Intl.DateTimeFormat('en-IN', {
            timeZone: 'Asia/Kolkata',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).format(lastUpdated)
        : '--:--:--';

    return (
        <div
            className={`w-full transition-all duration-300 ${
                isActuallyPinned
                    ? 'sticky top-14 sm:top-16 z-30 shadow-[0_12px_40px_-10px_rgba(99,102,241,0.2)] bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-1.5 rounded-[22px] border-2 border-indigo-500/50 dark:border-indigo-400/50'
                    : ''
            } ${className}`}
        >
            <GeometricAccordion
                title="INDEX CONSTITUENTS DAY P&L"
                badge={<Zap size={18} />}
                storageKey={STORAGE_KEYS.INDEX_CONSTITUENTS_PL_EXPANDED}
                headerRight={
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        {/* Live Feed Pulse */}
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40 text-[10px] font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                            <span className="hidden sm:inline">Live 2s</span>
                        </div>

                        {/* Last Updated IST Time in HH:MM:SS */}
                        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold font-mono bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 px-2 py-0.5 rounded-md border border-slate-200/80 dark:border-slate-700/80 shadow-xs">
                            <Clock size={11} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                            <span>IST {istTimeStr}</span>
                        </div>

                        {/* Pin to Top Button */}
                        <button
                            type="button"
                            onClick={handleTogglePin}
                            title={isActuallyPinned ? 'Unpin from Top' : 'Pin to Top of Page'}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] sm:text-[11px] font-black transition-all duration-200 border cursor-pointer ${
                                isActuallyPinned
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-400/30'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                        >
                            {isActuallyPinned ? (
                                <>
                                    <PinOff size={13} strokeWidth={2.5} />
                                    <span>Pinned</span>
                                </>
                            ) : (
                                <>
                                    <Pin size={13} strokeWidth={2.5} />
                                    <span>Pin</span>
                                </>
                            )}
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col gap-2.5 sm:gap-3 pt-1">
                    {/* SECTION 1: Three Separate Index Day P&L Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-2.5 lg:gap-3 items-stretch">
                        {SUPPORTED_SYMBOLS.map((sym) => (
                            <IndexPlCard
                                key={sym}
                                summary={summaries[sym]}
                                isActive={selectedSymbol === sym}
                                onSelect={() => handleSelectIndex(sym)}
                            />
                        ))}
                    </div>

                    {/* SECTION 2: Top 5 Gainers & Top 5 Losers (Side-by-Side) */}
                    <IndexTopMoversSection
                        summaries={summaries}
                        activeGlobalSymbol={selectedSymbol}
                        onSelectIndex={handleSelectIndex}
                    />
                </div>

            </GeometricAccordion>
        </div>
    );
});

IndexConstituentsPlSection.displayName = 'IndexConstituentsPlSection';
